import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "../../utils/toast";
import { EmptyState, Loading } from "../ui";
import { abroadStudentApi } from "../../api/abroadLoans";
import { errorMessageOf, problemOf } from "../../api/scholarships";
import { formatDuration } from "../scholarships/model";
import EligibilityNote, { PROFILE_PATH } from "../scholarships/student/EligibilityNote";
import { useAcademicProfile } from "../scholarships/student/model";
import StudentCallView from "../calls/StudentCallView";
import { programmeLabel } from "./model";
import { ABROAD_STUDENT_KEY, abroadApplicationPath, rulesSummary, useMyAbroadApplications, useStudentAbroadCall } from "./student";

// The student's current application to a call: one per call until withdrawn
const mineIn = (applications, callId) => applications.find((a) => a.callId === callId && a.studentStatus !== "WITHDRAWN");

// One abroad loan call for a student: what each programme asks for, who may apply, and where to start
export default function StudentCallPage() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: call, isPending, isError, error, refetch } = useStudentAbroadCall(callId);
  const { data: applications = [] } = useMyAbroadApplications();
  const { data: profile, isPending: profilePending } = useAcademicProfile();

  if (isPending || profilePending) return <Loading label="Loading…" />;
  if (isError)
    return problemOf(error)?.status === 404 ? (
      <EmptyState title="This loan call is not available" description="The link may be wrong, or it is no longer published." />
    ) : (
      <EmptyState title="Could not load it" onRefresh={refetch} />
    );

  const mine = mineIn(applications, callId);

  const start = async (programmeId) => {
    try {
      const { data } = await abroadStudentApi.start(callId, programmeId);
      queryClient.invalidateQueries({ queryKey: ABROAD_STUDENT_KEY });
      navigate(abroadApplicationPath(data.id));
    } catch (err) {
      console.error(`Failed to start an application to abroad loan call ${callId}:`, err);
      if (problemOf(err)?.code === "already-applied") {
        const { data: latest } = await abroadStudentApi.applications();
        const existing = mineIn(latest, callId);
        if (existing) return navigate(abroadApplicationPath(existing.id));
      }
      toast.error(errorMessageOf(err, "Could not start your application. Please try again."));
    }
  };

  return (
    <StudentCallView
      title={call.name}
      subtitle={["Abroad loan", call.academicYearName, "study where you were admitted"].filter(Boolean).join(" · ")}
      aboutTitle="About this loan call"
      description={call.description}
      programmes={call.programmes.map(({ programme: p, requirements, eligibility }) => ({
        id: p.id,
        name: programmeLabel(p),
        meta: [p.levelName, formatDuration(p.durationMonths)].filter(Boolean).join(" · "),
        requirements,
        whoMayApply: (
          <p className="text-sm text-slate-700">
            <span className="font-medium">Who may apply:</span> {eligibility.length ? rulesSummary(eligibility).join("; ") : "any student"}
          </p>
        ),
      }))}
      closesAt={call.closesAt}
      open={call.open}
      mine={mine}
      applicationPath={abroadApplicationPath}
      // A profile is needed to apply; a student whose profile misses a programme's rules may still apply, flagged to HEC
      canStart={call.open && !mine && Boolean(profile)}
      onStart={start}
      beforeStart={!profile && <EligibilityNote eligibility={{ profileRequired: true }} />}
      afterStart={
        profile && (
          <p className="text-xs text-slate-500">
            Your studies come from your{" "}
            <Link to={PROFILE_PATH} className="font-medium text-brand hover:underline">
              academic profile
            </Link>
            .
          </p>
        )
      }
    />
  );
}
